import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Target, Loader2, Star, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function TechnicalSkills({ childId, sport, childName }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [skills, setSkills] = useState(null);
  const [ratings, setRatings] = useState({});
  const [latestRating, setLatestRating] = useState(null);
  const [notes, setNotes] = useState('');
  const [expanded, setExpanded] = useState(true);

  const loadSkills = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/technical-skills/${sport}`);
      setSkills(response.data.skills);
    } catch (error) {
      console.error('Failed to load skills:', error);
    } finally {
      setLoading(false);
    }
  }, [sport]);

  const loadLatestRating = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/technical-ratings/${childId}/latest`);
      if (response.data.rating) {
        setLatestRating(response.data.rating);
        setRatings(response.data.rating.ratings || {});
        setNotes(response.data.rating.notes || '');
      }
    } catch (error) {
      // No previous rating - expected for new athletes
    }
  }, [childId]);

  useEffect(() => {
    if (sport) {
      loadSkills();
      loadLatestRating();
    }
  }, [sport, childId, loadSkills, loadLatestRating]);

  const handleRatingChange = (skillId, value) => {
    setRatings(prev => ({ ...prev, [skillId]: value }));
  };

  const saveRatings = async () => {
    if (Object.keys(ratings).length === 0) {
      toast.error('Please rate at least one skill');
      return;
    }
    try {
      setSaving(true);
      await axios.post(`${API}/technical-ratings`, {
        child_id: childId,
        sport: sport,
        ratings: ratings,
        notes: notes
      });
      toast.success('Technical ratings saved!');
      loadLatestRating();
    } catch (error) {
      toast.error('Failed to save ratings');
    } finally {
      setSaving(false);
    }
  };

  const StarRating = ({ skillId, value }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          onClick={() => handleRatingChange(skillId, star)}
          className={`p-0.5 transition-colors ${
            star <= (ratings[skillId] || 0)
              ? 'text-yellow-400'
              : 'text-zinc-600 hover:text-zinc-400'
          }`}
        >
          <Star className="h-5 w-5" fill={star <= (ratings[skillId] || 0) ? 'currentColor' : 'none'} />
        </button>
      ))}
    </div>
  );

  if (!sport) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6 text-center">
        <Target className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">Select a primary sport to view technical skills</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#02b5ff] animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden" data-testid="technical-skills">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Target className="h-5 w-5 text-[#02b5ff]" />
          <h3 className="text-lg font-bold text-white">Technical Skills - {sport}</h3>
          {latestRating && (
            <span className="text-sm bg-[#02b5ff]/20 text-[#02b5ff] px-2 py-1 rounded">
              Avg: {latestRating.overall_average}/5
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="h-5 w-5 text-zinc-400" /> : <ChevronDown className="h-5 w-5 text-zinc-400" />}
      </button>

      {expanded && skills && (
        <div className="p-4 border-t border-zinc-800 space-y-6">
          {skills.categories?.map((category, catIdx) => (
            <div key={catIdx}>
              <h4 className="text-sm font-bold uppercase tracking-widest text-[#02b5ff] mb-3">
                {category.name}
                {latestRating?.category_averages?.[category.name] && (
                  <span className="ml-2 text-xs text-zinc-500 font-normal">
                    (Avg: {latestRating.category_averages[category.name]})
                  </span>
                )}
              </h4>
              <div className="space-y-3">
                {category.skills.map((skill, skillIdx) => (
                  <div key={skillIdx} className="flex items-center justify-between bg-zinc-800/30 p-3 rounded">
                    <div>
                      <p className="text-white font-medium">{skill.name}</p>
                      <p className="text-xs text-zinc-500">{skill.description}</p>
                    </div>
                    <StarRating skillId={skill.id} value={ratings[skill.id]} />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div>
            <label className="text-sm font-bold uppercase tracking-widest text-zinc-400 mb-2 block">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional observations..."
              className="w-full bg-zinc-950 border border-zinc-800 rounded p-3 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#02b5ff]"
              rows={2}
            />
          </div>

          <Button
            onClick={saveRatings}
            disabled={saving}
            className="w-full bg-[#02b5ff] hover:bg-[#0099dd] text-white"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Technical Ratings
          </Button>

          {latestRating && (
            <p className="text-xs text-zinc-500 text-center">
              Last rated by {latestRating.rated_by_name} on {new Date(latestRating.rating_date).toLocaleDateString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
